// ChangelogPage — "What's new", público, en /changelog.
//
// Por qué existe: el cliente pidió poder mirar en línea qué se va arreglando,
// qué problemas aparecieron y cómo se resolvieron. Cada entrada trae PROBLEMA y
// SOLUCIÓN por separado a propósito: un changelog que sólo dice "arreglado X"
// no deja aprender nada de lo que pasó, y el punto de esto —según el pedido— es
// perfeccionar la plataforma, no anunciar.
//
// Los datos salen de public/changelog.json (mismo patrón que las notas de
// parche de DGTO). Regla: todo cambio que el cliente pueda notar deja su nota
// en ese archivo, en el MISMO commit que el cambio.
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { PublicPageLayout } from '../components/PublicPageLayout';

type ChangeType = 'new' | 'improvement' | 'fix';

interface Change {
  type: ChangeType;
  title: string;
  problem?: string;
  solution?: string;
}
interface Release {
  date: string;
  title: string;
  summary?: string;
  changes: Change[];
}

const LABELS: Record<ChangeType, string> = { new: 'New', improvement: 'Improved', fix: 'Fixed' };
const TONES: Record<ChangeType, { fg: string; bg: string; bd: string }> = {
  new: { fg: '#065F46', bg: '#D1FAE5', bd: '#6EE7B7' },
  improvement: { fg: '#3730A3', bg: '#E0E7FF', bd: '#C7D2FE' },
  fix: { fg: '#9A3412', bg: '#FFEDD5', bd: '#FDBA74' },
};

const Release = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
`;

const ReleaseHead = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const DateTag = styled.time`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.xs};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 4px;
`;

const ReleaseTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.3;
`;

const Summary = styled.p`
  margin-top: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
`;

const Item = styled.article`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
`;

const Tag = styled.span<{ $type: ChangeType }>`
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 999px;
  color: ${({ $type }) => TONES[$type].fg};
  background: ${({ $type }) => TONES[$type].bg};
  border: 1px solid ${({ $type }) => TONES[$type].bd};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const ItemTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.4;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const Block = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const BlockLabel = styled.span`
  display: block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 2px;
`;

const BlockText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.65;
`;

const Note = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.6;
`;

export function ChangelogPage() {
  const [releases, setReleases] = useState<Release[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    fetch('/changelog.json')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data) => setReleases(Array.isArray(data) ? data : []))
      .catch(() => setFailed(true));
  }, []);

  return (
    <PublicPageLayout
      title="What's New"
      subtitle="Every change we ship — what went wrong, and how we fixed it."
    >
      {failed && <Note>We couldn't load the changelog right now. Please try again in a moment.</Note>}
      {!failed && !releases && <Note>Loading…</Note>}
      {releases?.length === 0 && <Note>Nothing published yet.</Note>}

      {releases?.map((release) => (
        <Release key={release.date + release.title}>
          <ReleaseHead>
            <DateTag dateTime={release.date}>
              {new Date(`${release.date}T00:00:00`).toLocaleDateString('en-US', {
                year: 'numeric', month: 'long', day: 'numeric',
              })}
            </DateTag>
            <ReleaseTitle>{release.title}</ReleaseTitle>
            {release.summary && <Summary>{release.summary}</Summary>}
          </ReleaseHead>

          {release.changes.map((change) => (
            <Item key={change.title}>
              <Tag $type={change.type}>{LABELS[change.type] ?? change.type}</Tag>
              <ItemTitle>{change.title}</ItemTitle>
              {change.problem && (
                <Block>
                  <BlockLabel>What was happening</BlockLabel>
                  <BlockText>{change.problem}</BlockText>
                </Block>
              )}
              {change.solution && (
                <Block>
                  <BlockLabel>What we changed</BlockLabel>
                  <BlockText>{change.solution}</BlockText>
                </Block>
              )}
            </Item>
          ))}
        </Release>
      ))}
    </PublicPageLayout>
  );
}
